<script setup>
import { useDisplay } from 'vuetify'
import { computed, ref } from 'vue'
import { canViewArchivedStillFrame, canViewHeatmap, canViewLongTimeExposure, canViewStillFrame } from '@/composables/permissionHelper'
import stillFrameIconUrl from '@/assets/canvasElementProviderIcons/stillFrame.png'
import heatmapIconUrl from '@/assets/canvasElementProviderIcons/heatmap.png'
import lteIconUrl from '@/assets/canvasElementProviderIcons/lte.png'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  elementProvider: { type: String, default: 'stillFrame' },
})
const emit = defineEmits(['update:elementProvider', 'openShareDialog'])

const { mobile, width, height } = useDisplay()
const { t } = useI18n()

const menuExpanded = ref(false)

const availableElementProviders = computed(() => {
  const providers = [
    {
      id: 'stillFrame',
      hasPermission: canViewStillFrame(),
      sprite: stillFrameIconUrl,
      title: t('canvas.element_providers.stillFrame'),
      short_title: t('canvas.element_providers.stillFrame_short'),
    },
    {
      id: 'artifact.heatmap',
      hasPermission: canViewHeatmap(),
      sprite: heatmapIconUrl,
      title: t('canvas.element_providers.artifact.heatmap'),
      short_title: t('canvas.element_providers.artifact.heatmap_short'),
    },
    {
      id: 'artifact.longTimeExposure',
      hasPermission: canViewLongTimeExposure(),
      sprite: lteIconUrl,
      title: t('canvas.element_providers.artifact.longTimeExposure'),
      short_title: t('canvas.element_providers.artifact.longTimeExposure_short'),
    },
    {
      id: 'artifact.archivedStillFrame',
      hasPermission: canViewArchivedStillFrame(),
      sprite: stillFrameIconUrl,
      title: t('canvas.element_providers.artifact.archivedStillFrame'),
      short_title: t('canvas.element_providers.artifact.archivedStillFrame_short'),
    },
  ]
  return providers.filter(({ hasPermission }) => hasPermission)
})

const currentlyActiveImageSourceSprite = computed(() => {
  if (!props.elementProvider) return stillFrameIconUrl
  switch (props.elementProvider) {
    case 'stillFrame':
      return stillFrameIconUrl
    case 'artifact.heatmap':
      return heatmapIconUrl
    case 'artifact.longTimeExposure':
      return lteIconUrl
    case 'artifact.archivedStillFrame':
      return stillFrameIconUrl
  }
  return stillFrameIconUrl
})

/**
 * Switch between the still frame and heatmap element providers
 * @returns {void}
 */
const switchElementProvider = () => {
  if (mobile.value) {
    menuExpanded.value = true
    return
  }

  const heatmapAndStillFrameProviders = availableElementProviders.value.filter(
    provider => provider.id === 'stillFrame' || provider.id === 'artifact.heatmap'
  )
  const currentIndex = heatmapAndStillFrameProviders.findIndex(provider => provider.id === props.elementProvider)
  if (currentIndex === -1) return

  const nextIndex = (currentIndex + 1) % heatmapAndStillFrameProviders.length
  const nextProvider = heatmapAndStillFrameProviders[nextIndex]
  if (!nextProvider) return

  emit('update:elementProvider', nextProvider.id)
}
</script>

<template>
  <v-hover v-slot="{ isHovering, props: hoverProps }">
    <v-layout-card
      v-bind="hoverProps"
      id="element-provider"
      class="pa-1 ma-4 position-absolute"
      variant="elevated"
      style="transition: all 0.2s ease-in-out; padding: 2px"
      width="93px"
      @click="menuExpanded = true"
    >
      <v-img
        id="menu-activator"
        :src="currentlyActiveImageSourceSprite"
        aspect-ratio="1"
        gradient="to top, rgba(0,0,0,0.5), rgba(0,0,0,0.2), rgba(0,0,0,0.0)"
        class="align-end rounded-lg border-"
        @click="!mobile && switchElementProvider()"
      >
        <h5 class="mb-1 text-white text-center text-caption font-weight-medium">
          <v-icon
            v-if="!isHovering"
            icon="mdi-image-multiple-outline"
            size="small"
          />
          {{ isHovering ? $t(`canvas.element_providers.${props.elementProvider}_short`) : $t('canvas.element_providers.image') }}
        </h5>
      </v-img>

      <v-menu
        v-if="mobile && availableElementProviders.length > 1"
        v-model="menuExpanded"
        activator="#menu-activator"
        :open-on-click="true"
        :close-on-content-click="true"
        location="top start"
      >
        <v-sheet
          class="my-2"
          max-width="200px"
        >
          <v-row
            density="comfortable"
            :no-gutters="true"
            class="ma-0 pa-0"
          >
            <v-col
              v-for="availableElementProvider in availableElementProviders"
              :key="availableElementProvider.id"
              :cols="12"
            >
              <v-card
                :variant="availableElementProvider.id === elementProvider ? 'outlined' : 'flat'"
                class="ma-2 d-flex justify-start align-center ga-2"
                @click="() => $emit('update:elementProvider', availableElementProvider.id)"
              >
                <v-img
                  width="100%"
                  max-width="80px"
                  aspect-ratio="1"
                  :src="availableElementProvider.sprite"
                  class="align-end"
                />
                <p
                  class="text-center flex-fill"
                  style="white-space: normal; overflow: hidden; word-break: normal"
                >
                  {{ availableElementProvider.title }}
                </p>
              </v-card>
            </v-col>
          </v-row>
        </v-sheet>
      </v-menu>

      <!-- elementprovider -->
      <v-menu
        v-if="!mobile && availableElementProviders.length > 1"
        v-bind="hoverProps"
        v-model="menuExpanded"
        activator="#menu-activator"
        :open-on-click="mobile"
        :open-on-hover="!mobile"
        :close-on-content-click="false"
        :location="mobile && height > width ? 'top start' : 'end center'"
      >
        <v-sheet class="mx-2">
          <v-row
            density="comfortable"
            :no-gutters="true"
            class="ma-1 mb-0 ga-1"
          >
            <v-col
              v-for="availableElementProvider in availableElementProviders"
              :key="availableElementProvider.id"
              class="ma-0 pa-1 text-truncate"
            >
              <v-tooltip
                activator="parent"
                location="top"
                :text="availableElementProvider.title"
                close-on-content-click
                scroll-strategy="close"
              >
                <template #activator="{ props: tooltipProps }">
                  <v-img
                    v-bind="tooltipProps"
                    aspect-ratio="1"
                    width="60px"
                    :src="availableElementProvider.sprite"
                    class="align-end rounded cursor-pointer"
                    @click="() => $emit('update:elementProvider', availableElementProvider.id)"
                  />
                  <v-card-subtitle
                    class="px-0 text-center"
                    style="white-space: normal; overflow: hidden; word-break: normal"
                  >
                    {{ availableElementProvider.short_title }}
                  </v-card-subtitle>
                </template>
              </v-tooltip>
            </v-col>
          </v-row>
        </v-sheet>
      </v-menu>
    </v-layout-card>
  </v-hover>
</template>

<style scoped></style>
